// Filename rules shared by the server (PATCH /file/:fileId validation) and the
// client (inline rename editor / rename dialog) so a name the UI accepts is a
// name the server accepts, and the error copy is identical on both sides.
//
// A rename only changes the display name stored in the DB (the object in MinIO
// is keyed by file id and never moves), so the rules are about what a sane
// download / export / attachment name looks like:
//   - trimmed, non-empty, at most 255 UTF-8 bytes
//   - no path separators (/ or \) and none of the Unicode control / invisible
//     classes: Cc (C0 NUL ... US, DEL, C1 U+0080 ... U+009F), Cf (every format
//     character — zero-width space/joiners, bidi marks and overrides incl. RTLO,
//     isolates, word joiner, soft hyphen, BOM, tag characters ...) and the line /
//     paragraph separators Zl / Zp (U+2028 / U+2029) — any of which can make a name
//     render as a different name or type in lists, export labels and attachment
//     names. Ordinary non-ASCII text, including NBSP (U+00A0, class Zs), is allowed.
//   - not "." or ".."
//   - the extension (last ".ext" segment) must stay the same, case-insensitively;
//     a file without an extension must stay without one

export const MAX_FILENAME_BYTES = 255

// Unicode property classes (ES2018) rather than a hand-maintained list of ranges.
const FORBIDDEN_CHARS = /[/\\\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/u

/**
 * Split a filename into its stem and extension the way `path.extname` does:
 * the extension is the last ".ext" segment when the dot is not the first
 * character; ".gitignore" and "README" have no extension.
 */
export function splitExtension(filename) {
  const name = String(filename ?? '')
  const dot = name.lastIndexOf('.')
  if (dot <= 0) return { stem: name, extension: '' }
  return { stem: name.slice(0, dot), extension: name.slice(dot) }
}

export function utf8ByteLength(value) {
  let bytes = 0
  for (const char of String(value)) {
    const codePoint = char.codePointAt(0)
    if (codePoint < 0x80) bytes += 1
    else if (codePoint < 0x800) bytes += 2
    else if (codePoint < 0x10000) bytes += 3
    else bytes += 4
  }
  return bytes
}

/** Trim the surrounding whitespace; everything else is left as typed. */
export function normalizeFilename(filename) {
  return String(filename ?? '').trim()
}

/**
 * Validate a proposed new name against the current one. Returns `null` when
 * valid, otherwise a human-readable reason (the same text the UI shows).
 */
export function getRenameFilenameError(newFilename, currentFilename) {
  if (typeof newFilename !== 'string') return 'File name is required.'
  const name = normalizeFilename(newFilename)
  if (!name) return 'File name cannot be empty.'
  if (name === '.' || name === '..') return 'File name is not valid.'
  if (FORBIDDEN_CHARS.test(name)) return 'File names cannot contain / or \\ or control characters.'
  if (utf8ByteLength(name) > MAX_FILENAME_BYTES) return `File name must be at most ${MAX_FILENAME_BYTES} bytes.`

  const { extension: currentExtension } = splitExtension(currentFilename)
  const { extension: newExtension } = splitExtension(name)
  if (currentExtension.toLowerCase() !== newExtension.toLowerCase()) {
    if (!currentExtension) return 'This file has no extension, so the new name cannot have one.'
    return `The extension cannot be changed — it must stay ${currentExtension}.`
  }
  return null
}

/**
 * UI editors let the user edit only the stem while the extension is locked:
 * build the full candidate name from a stem + the current file's extension.
 */
export function buildRenamedFilename(stem, currentFilename) {
  const { extension } = splitExtension(currentFilename)
  return `${normalizeFilename(stem)}${extension}`
}

/** Validate a stem typed in a locked-extension editor. `null` when valid, else the reason. */
export function getRenameStemError(stem, currentFilename) {
  if (!normalizeFilename(stem)) return 'File name cannot be empty.'
  return getRenameFilenameError(buildRenamedFilename(stem, currentFilename), currentFilename)
}

export function isValidRenameFilename(newFilename, currentFilename) {
  return getRenameFilenameError(newFilename, currentFilename) === null
}
